import React, { useState } from 'react';

import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import { useGetSuppliers } from '~/hooks/apis/tours/useTourSuppliers';
import useDebounced from '~/hooks/useDebounced';

import { Supplier } from '~/types/tours/supplier';

interface Props {
  open: boolean;
  title?: string;
  description?: string;
  onClose: () => void;
  onSelect: (supplier: Supplier) => void;
}

export default function SupplierPickerDialog({ open, title, description, onClose, onSelect }: Props) {
  const [inputValue, setInputValue] = useState('');

  const name = useDebounced(inputValue.trim(), SEARCH_DEBOUNCE_MS);
  const [supplier, setSupplier] = useState<Supplier | null>(null);

  const { data: suppliers = [], isLoading, error } = useGetSuppliers(name);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title ?? 'Choose a supplier'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {!!description && <DialogContentText>{description}</DialogContentText>}

          <Autocomplete
            value={supplier}
            onChange={(_event, next) => setSupplier(next)}
            inputValue={inputValue}
            onInputChange={(_event, next) => setInputValue(next)}
            options={suppliers}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            loading={isLoading}
            filterOptions={(all) => all}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supplier"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {!!error && <Alert severity="error">{error.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!supplier} onClick={() => onSelect(supplier)}>
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
}
